import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import ReactFlow, { 
  Background, 
  Controls, 
  Node, 
  Edge, 
  Position, 
  MarkerType, 
  NodeMouseHandler,
  useReactFlow
} from 'reactflow';
import 'reactflow/dist/style.css';
import { AITreeResponse, WordNode } from '../types';
import dagre from 'dagre';
import { 
  addNode, 
  deleteNode, 
  selectNode,
  aiGenerateX10,
  aiGenerateX60
} from '../services/tauri-api';
import ChatDrawer from './ChatDrawer';

// ============================================================================
// 型定義
// ============================================================================

interface FlowTreeProps {
  tree: AITreeResponse;
  onNodeSelect?: (path: WordNode[]) => void;
  showAllSiblings: boolean;
  sessionId?: string;
  onTreeReload?: () => void;
  onToggleShowAll?: (showAll: boolean) => void;
  focusNodeId?: string;
  onNodeSelected?: (nodeId: string) => void;
  loadVersion?: number; // setLoading(false) 完了直後のバージョン
  autoOpenChatAndGenerate?: boolean; // 新しいプロパティ
  sidebarOpen?: boolean; // サイドバーの開閉状態
}

interface ContextMenuProps {
  x: number;
  y: number;
  nodeId: string;
  onClose: () => void;
  onAddChild: (nodeId: string) => void;
  onAddSibling: (nodeId: string) => void;
  onDelete: (nodeId: string) => void;
  showGenerate?: boolean;
  onGenerateCandidates?: (nodeId: string) => void;
}

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNodeSelect: (nodeId: string) => void;
  tree: AITreeResponse;
}

interface AddNodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (word: string) => void;
  parentNodeId: string;
  parentNodeWord: string;
  nodeType?: 'child' | 'text';
}

interface SearchResult {
  nodeId: string;
  word: string;
  prob: number;
  path: string;
}

interface FloatingActionButtonProps {
  onToggleSiblings: () => void;
  onSearch: () => void;
  onAddTextNode: () => void;
  onAddChildNode: () => void;
  onAiGenerateX10: () => void;
  onToggleChat: () => void;
  showAllSiblings: boolean;
}

// ============================================================================
// 定数・設定
// ============================================================================

const NODE_CONFIG = {
  width: 160,
  height: 64,
  borderRadius: 12,
} as const;

// 文字数に応じてフォントサイズを調整する関数
const getFontSize = (text: string): string => {
  const length = text.length;
  if (length <= 4) return '14px';
  if (length <= 8) return '12px';
  if (length <= 12) return '11px';
  if (length <= 16) return '10px';
  if (length <= 20) return '9px';
  return '8px';
};

const COLORS = {
  selected: {
    background: '#F0FDF4',
    border: '#22C55E',
  },
  default: {
    background: '#FFFFFF',
    border: '#E5E7EB',
  },
  selectedPath: {
    border: '#3B82F6',
    shadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
  },
  probability: {
    text: '#1D4ED8',
    background: '#DBEAFE',
    border: '#93C5FD',
  },
  edge: {
    color: '#93C5FD',
    strokeWidth: 2,
  },
} as const;
// ノード間距離調整用。あとで修正する。
const DAGRE_CONFIG = {
  rankdir: 'LR' as const,
  nodesep: 20,
  ranksep: 60,
  edgesep: 20,
} as const;

const REACT_FLOW_CONFIG = {
  fitViewOptions: { padding: 0.2 },
  backgroundGap: 24,
  backgroundColor: '#e5e7eb',
} as const;

// ============================================================================
// ノード追加モーダルコンポーネント
// ============================================================================

const AddNodeModal: React.FC<AddNodeModalProps> = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  parentNodeId, 
  parentNodeWord,
  nodeType = 'child'
}) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // モーダルが開いたらフォーカス
  useEffect(() => {
    if (isOpen) {
      setInputValue('');
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // ESCキーで閉じる
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  const handleSubmit = () => {
    if (inputValue.trim()) {
      onConfirm(inputValue.trim());
      setInputValue('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const getModalTitle = () => {
    switch (nodeType) {
      case 'text': return 'テキストノードを追加';
      case 'child': return '子ノードを追加';
      default: return '子ノードを追加';
    }
  };

  const getPlaceholder = () => {
    switch (nodeType) {
      case 'text': return 'テキストノード名を入力...';
      case 'child': return '新しいノード名を入力...';
      default: return '新しいノード名を入力...';
    }
  };

  if (!isOpen) return null;

  // console.log('AddNodeModal レンダリング:', { isOpen, parentNodeId, parentNodeWord, nodeType });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {getModalTitle()}
          </h3>
          {nodeType === 'child' && (
            <p className="text-sm text-gray-600">
              親ノード: <span className="font-medium text-blue-600">"{parentNodeWord}"</span>
            </p>
          )}
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            ノード名
          </label>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={getPlaceholder()}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            maxLength={50}
          />
          <p className="text-xs text-gray-500 mt-1">
            {inputValue.length}/50文字
          </p>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            キャンセル
          </button>
          <button
            onClick={handleSubmit}
            disabled={!inputValue.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            追加
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// 検索モーダルコンポーネント
// ============================================================================

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose, onNodeSelect, tree }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // 検索ロジック
  const searchNodes = useCallback((query: string): SearchResult[] => {
    if (!query.trim()) {
      return [];
    }

    const results: SearchResult[] = [];
    const searchTerm = query.toLowerCase();

    const searchInTree = (node: WordNode, currentPath: string = '') => {
      const nodeWord = node.word || 'root';
      if (nodeWord.toLowerCase().includes(searchTerm)) {
        results.push({
          nodeId: node.path,
          word: nodeWord,
          prob: node.prob,
          path: currentPath || 'root',
        });
      }

      node.children.forEach(child => {
        const childPath = currentPath ? `${currentPath} → ${child.word}` : child.word;
        searchInTree(child, childPath);
      });
    };

    searchInTree(tree);
    return results;
  }, [tree]);

  // 検索クエリが変更された時の処理
  useEffect(() => {
    const results = searchNodes(searchQuery);
    setSearchResults(results);
  }, [searchQuery, searchNodes]);

  // モーダルが開いた時にフォーカス
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // ESCキーで閉じる
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  const handleResultClick = (nodeId: string) => {
    onNodeSelect(nodeId);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-96 max-h-96 flex flex-col">
        {/* ヘッダー */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold flex items-center">
            {/* search icon */}
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 text-gray-600">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.3-4.3"/>
            </svg>
            ノード検索
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="閉じる"
          >
            {/* close icon */}
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18"/>
              <path d="M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* 検索ボックス */}
        <div className="p-4 border-b">
          <input
            ref={inputRef}
            type="text"
            placeholder="ノード名を入力..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* 検索結果 */}
        <div className="flex-1 overflow-y-auto">
          {searchQuery.trim() ? (
            searchResults.length > 0 ? (
              <>
                <div className="px-4 py-2 text-sm text-gray-600 border-b">
                  検索結果 ({searchResults.length}件)
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {searchResults.map((result) => (
                    <button
                      key={result.nodeId}
                      onClick={() => handleResultClick(result.nodeId)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-medium text-gray-900">{result.word}</div>
                      <div className="text-sm text-gray-500">
                        {(result.prob * 100).toFixed(1)}% • {result.path}
                      </div>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="px-4 py-8 text-center text-gray-500">
                検索結果が見つかりません
              </div>
            )
          ) : (
            <div className="px-4 py-8 text-center text-gray-500">
              検索キーワードを入力してください
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// コンテキストメニューコンポーネント
// ============================================================================

const ContextMenu: React.FC<ContextMenuProps> = ({ 
  x, y, nodeId, onClose, onAddChild, onAddSibling, onDelete, showGenerate, onGenerateCandidates 
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as HTMLElement)) {
        onClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div 
      ref={menuRef}
      className="fixed bg-white border border-gray-200 shadow-lg rounded-md py-1 z-50 min-w-48"
      style={{ left: x, top: y }}
    >
      {showGenerate && (
        <button 
          className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center text-sm disabled:opacity-50"
          onClick={() => { onGenerateCandidates && onGenerateCandidates(nodeId); onClose(); }}
          disabled={(window as any).__FLOW_BUSY__ === true}
          title={(window as any).__FLOW_BUSY__ ? '処理中' : '候補を生成'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="mr-2 text-gray-600">
            <path d="M12 3l1.6 3.6L17 8.2l-3.4 1.6L12 13l-1.6-3.2L7 8.2l3.4-1.6L12 3z"/>
            <path d="M5 16l.8 1.8L8 18.6l-1.8.8L5 21l-.8-1.6L2.6 18.6l1.6-.8L5 16z"/>
            <path d="M19 14l1 2.2 2.2 1-2.2 1L19 20l-1-1.8-2.2-1 2.2-1L19 14z"/>
          </svg>
          候補を生成
        </button>
      )}
      {showGenerate && <hr className="my-1" />}
      <button 
        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center text-sm disabled:opacity-50"
        onClick={() => { onAddChild(nodeId); onClose(); }}
        disabled={(window as any).__FLOW_BUSY__ === true}
        title={(window as any).__FLOW_BUSY__ ? '処理中' : '子ノードを追加'}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="mr-2 text-gray-600">
          <path d="M12 5v14"/>
          <path d="M5 12h14"/>
        </svg>
        子ノードを追加
      </button>
      {/* <button 
        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center text-sm"
        onClick={() => { onAddSibling(nodeId); onClose(); }}
      >
        <span className="mr-2">👥</span>
        兄弟ノードを追加
      </button> */}
      <hr className="my-1" />
      <button 
        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center text-red-600 text-sm disabled:opacity-50"
        onClick={() => { onDelete(nodeId); onClose(); }}
        disabled={(window as any).__FLOW_BUSY__ === true}
        title={(window as any).__FLOW_BUSY__ ? '処理中' : 'ノードを削除'}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="mr-2">
          <polyline points="3 6 5 6 21 6"/>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
          <path d="M10 11v6"/>
          <path d="M14 11v6"/>
        </svg>
        ノードを削除
      </button>
      <hr className="my-1" />
      <button 
        className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center text-gray-500 text-sm"
        onClick={onClose}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="mr-2">
          <path d="M18 6 6 18"/>
          <path d="M6 6l12 12"/>
        </svg>
        閉じる
      </button>
    </div>
  );
};

// ============================================================================
// フローティングアクションボタンコンポーネント
// ============================================================================

const FloatingActionButton: React.FC<FloatingActionButtonProps & { isBusy?: boolean }> = ({
  onToggleSiblings,
  onSearch,
  onAddTextNode,
  onAddChildNode,
  onAiGenerateX10,
  onToggleChat,
  showAllSiblings,
  isBusy = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="fixed bottom-24 right-6 z-[60]">
      {/* サブボタン群 */}
      {isExpanded && (
        <div className="absolute bottom-16 right-0 space-y-2">
          {/* チャットボタン */}
          <button 
            className="w-12 h-12 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 flex items-center justify-center transition-all duration-200"
            onClick={() => { onToggleChat(); setIsExpanded(false); }}
            title="チャット履歴"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </button>
          
          <button 
            className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 ${
              isBusy ? 'bg-purple-400 opacity-60 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}
            onClick={() => { if (!isBusy) { onAiGenerateX10(); setIsExpanded(false); } }}
            title={isBusy ? '処理中' : 'AI生成×10'}
          >
            {isBusy ? (
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
            ) : (
              <div className="flex flex-col items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3l1.6 3.6L17 8.2l-3.4 1.6L12 13l-1.6-3.2L7 8.2l3.4-1.6L12 3z"/>
                  <path d="M5 16l.8 1.8L8 18.6l-1.8.8L5 21l-.8-1.6L2.6 18.6l1.6-.8L5 16z"/>
                  <path d="M19 14l1 2.2 2.2 1-2.2 1L19 20l-1-1.8-2.2-1 2.2-1L19 14z"/>
                </svg>
                <span className="text-xs font-bold leading-none">×10</span>
              </div>
            )}
          </button>
          <button 
            className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 ${
              isBusy ? 'bg-green-400 opacity-60 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
            onClick={() => { if (!isBusy) { onAddChildNode(); setIsExpanded(false); } }}
            title={isBusy ? '処理中' : '子ノード追加'}
          >
            {isBusy ? (
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14"/>
                <path d="M5 12h14"/>
              </svg>
            )}
          </button>
          <button 
            className="w-12 h-12 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 flex items-center justify-center transition-all duration-200"
            onClick={() => { onSearch(); setIsExpanded(false); }}
            title="ノード検索"
          >
            {/* search icon */}
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.3-4.3"/>
            </svg>
          </button>
          <button 
            className={`w-12 h-12 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 ${
              showAllSiblings 
                ? 'bg-gray-600 hover:bg-gray-700' 
                : 'bg-orange-600 hover:bg-orange-700'
            }`}
            onClick={() => { onToggleSiblings(); setIsExpanded(false); }}
            title={showAllSiblings ? "主要なもののみ表示" : "すべてを表示"}
          >
            {/* toggle icon */}
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="7" width="20" height="10" rx="5"/>
              <circle cx={showAllSiblings ? 7 : 17} cy="12" r="3"/>
            </svg>
          </button>
        </div>
      )}
      
      {/* メインボタン */}
      <button
        className={`relative w-14 h-14 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all duration-200 ${
          isExpanded ? 'rotate-45' : 'rotate-0'
        }`}
        onClick={() => setIsExpanded(!isExpanded)}
        title={'メニューを開く'}
      >
        <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      </button>
    </div>
  );
};

// ============================================================================
// ユーティリティ関数
// ============================================================================

/**
 * ノードの表示・非表示を制御する関数
 * 親コンテキストを考慮して再帰的に処理
 */
const updateNodeVisibility = (
  node: WordNode,
  showAllSiblings: boolean,
  parentIsSelected: boolean = false,
  parentHasSelectedChild: boolean = false
): void => {
  // ルートノードは常に表示（パスはドット区切り。'0' はルート、'0.x' は子）
  if (!node.path.includes('.')) {
    node.isVisible = true;
  } else {
    // ルート以外のノードは、トグルの状態に基づいて表示・非表示を決定
    if (showAllSiblings) {
      // トグルがONの場合、全てのノードを表示
      node.isVisible = true;
    } else {
      // トグルがOFFの場合の可視性ルール
      // 1) 自身が選択済みなら表示
      // 2) 親が選択済み かつ 親に選択済みの子がいない場合は、親の子全員を表示
      // 3) それ以外は非表示
      if (node.is_selected) {
        node.isVisible = true;
      } else if (parentIsSelected && !parentHasSelectedChild) {
        node.isVisible = true;
      } else {
        node.isVisible = false;
      }
    }
  }
  
  // 子ノードの表示・非表示を更新
  const hasSelectedChild = node.children.some(c => c.is_selected);
  node.children.forEach(child => {
    updateNodeVisibility(child, showAllSiblings, node.is_selected, hasSelectedChild);
  });
};

/**
 * ノードからルートまでのパスを取得する関数
 */
const findPathToRoot = (
  targetPath: string, 
  node: WordNode, 
  path: WordNode[] = []
): WordNode[] | null => {
  if (node.path === targetPath) {
    return [...path, node];
  }
  
  for (const child of node.children) {
    const result = findPathToRoot(targetPath, child, [...path, node]);
    if (result) return result;
  }
  
  return null;
};

// ============================================================================
// ノード・エッジ生成関数
// ============================================================================

/**
 * ノードのラベルコンポーネントを生成
 */
const createNodeLabel = (
  node: WordNode,
  onAddChild?: (nodeId: string) => void,
  onGenerateCandidates?: (nodeId: string) => void
) => {
  const isLeafNode = node.children.length === 0; // 末端ノードの場合に制限(残しとく)
  // const isLeafNode = true;
  // console.log('ノード:', node.word, 'isLeafNode:', isLeafNode, 'children:', node.children.length);
  
  return (
    <div className="w-full h-full px-3 py-2 text-center relative group flex flex-col items-center justify-center">
      {/* テキストコンテンツをdivでラップ */}
      <div className="w-full">
        <div 
          className="font-semibold break-words whitespace-normal"
          style={{
            fontSize: getFontSize(node.word || 'root'),
            lineHeight: '1.2',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            hyphens: 'auto'
          }}
        >
          {node.word || 'root'}
        </div>
        <div 
          className="text-xs mt-1 inline-block px-2 py-0.5 rounded-full border"
          style={{
            color: COLORS.probability.text,
            backgroundColor: COLORS.probability.background,
            borderColor: COLORS.probability.border,
          }}
        >
          {(node.prob * 100).toFixed(1)}%
        </div>
      </div>
      
      {/* ホバー時ボタン（葉: 候補生成/黄, それ以外: 子追加/青） */}
      {(onAddChild || onGenerateCandidates) && (
        <button
          className={`absolute -right-12 top-1/2 transform -translate-y-1/2 w-6 h-6 rounded-full shadow-md flex items-center justify-center text-sm font-bold transition-opacity duration-700 hover:scale-110 opacity-0 group-hover:opacity-100 leading-none z-50 ${
            (window as any).__FLOW_BUSY__
              ? (isLeafNode ? 'bg-yellow-300' : 'bg-blue-400') + ' text-white cursor-not-allowed'
              : (isLeafNode ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-blue-600 hover:bg-blue-700') + ' text-white'
          }`}
          onClick={(e) => {
            // console.log('+ボタンクリック:', node.path);
            e.stopPropagation();
            e.preventDefault();
            if ((window as any).__FLOW_BUSY__) return;
            if (isLeafNode && onGenerateCandidates) {
              onGenerateCandidates(node.path);
            } else if (onAddChild) {
              onAddChild(node.path);
            }
          }}
          onMouseDown={(e) => {
            // console.log('+ボタンマウスダウン:', node.path);
            e.stopPropagation();
          }}
          title={(window as any).__FLOW_BUSY__ ? '処理中' : (isLeafNode ? '候補を生成' : '子ノードを追加')}
        >
          { (window as any).__FLOW_BUSY__ ? (
            <svg className="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
          ) : (
            isLeafNode ? (
              // sparkles icon
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3l1.6 3.6L17 8.2l-3.4 1.6L12 13l-1.6-3.2L7 8.2l3.4-1.6L12 3z"/>
                <path d="M5 16l.8 1.8L8 18.6l-1.8.8L5 21l-.8-1.6L2.6 18.6l1.6-.8L5 16z"/>
                <path d="M19 14l1 2.2 2.2 1-2.2 1L19 20l-1-1.8-2.2-1 2.2-1L19 14z"/>
              </svg>
            ) : (
              // plus icon
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14"/>
                <path d="M5 12h14"/>
              </svg>
            )
          ) }
        </button>
      )}
    </div>
  );
};

/**
 * ノードのスタイルを生成
 */
const createNodeStyle = (node: WordNode) => ({
  width: NODE_CONFIG.width,
  height: NODE_CONFIG.height,
  borderRadius: NODE_CONFIG.borderRadius,
  backgroundColor: node.is_selected ? COLORS.selected.background : COLORS.default.background,
  borderColor: node.is_selected ? COLORS.selected.border : COLORS.default.border,
  borderWidth: 1,
});

/**
 * WordNodeツリーをReactFlowのノード・エッジ配列に変換
 */
const toFlowFlat = (
  root: WordNode,
  onAddChild?: (nodeId: string) => void,
  onGenerateCandidates?: (nodeId: string) => void
): { nodes: Node[]; edges: Edge[] } => {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  const walk = (node: WordNode): void => {
    // isVisibleがfalseの場合はスキップ
    if (node.isVisible === false) {
      return;
    }

    nodes.push({
      id: node.path,
      position: { x: 0, y: 0 },
      data: { label: createNodeLabel(node, onAddChild, onGenerateCandidates) },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      style: createNodeStyle(node),
    });
    
    node.children.forEach((child) => {
      // isVisibleがfalseの場合はエッジも作成しない
      if (child.isVisible === false) {
        return;
      }
      
      edges.push({
        id: `${node.path}-${child.path}`,
        source: node.path,
        target: child.path,
        type: 'smoothstep',
        markerEnd: { 
          type: MarkerType.ArrowClosed, 
          color: COLORS.edge.color 
        },
        style: { 
          stroke: COLORS.edge.color, 
          strokeWidth: COLORS.edge.strokeWidth 
        },
      });
      
      walk(child);
    });
  };

  walk(root);
  return { nodes, edges };
};

// ============================================================================
// レイアウト関数
// ============================================================================

/**
 * Dagreを使用してノードのレイアウトを適用
 */
const applyDagreLayout = (nodes: Node[], edges: Edge[]): Node[] => {
  const g = new dagre.graphlib.Graph();
  g.setGraph(DAGRE_CONFIG);
  g.setDefaultEdgeLabel(() => ({}));

  nodes.forEach((n) => g.setNode(n.id, { 
    width: NODE_CONFIG.width, 
    height: NODE_CONFIG.height 
  }));
  edges.forEach((e) => g.setEdge(e.source as string, e.target as string));

  dagre.layout(g);

  return nodes.map((n) => {
    const { x, y } = g.node(n.id);
    return {
      ...n, 
      position: { 
        x: x - NODE_CONFIG.width / 2, 
        y: y - NODE_CONFIG.height / 2 
      }
    };
  });
};

// ============================================================================
// メインコンポーネント
// ============================================================================

const FlowTree: React.FC<FlowTreeProps> = ({ tree, onNodeSelect, showAllSiblings, sessionId, onTreeReload, onToggleShowAll, focusNodeId, onNodeSelected, loadVersion, autoOpenChatAndGenerate = false, sidebarOpen = false }) => {
  const [selectedNodePath, setSelectedNodePath] = useState<string | null>(null);
  const [selectedPath, setSelectedPath] = useState<WordNode[]>([]);
  const [isBusy, setIsBusy] = useState(false);
  const [isChatDrawerOpen, setIsChatDrawerOpen] = useState(false);
  const reactFlowViewportRef = useRef<{ focusedForTreeVersion?: string } | null>({});
  const isDummyEndError = useCallback((error?: string | null) => {
    if (!error) return false;
    return error.includes('ダミー候補が返されたため');
  }, []);
  
  // デバッグテスト用: デフォルトで isBusy をONにできる仕組み
  // 有効条件: URLに ?busy=1 / localStorage.forceBusy === '1' / window.__FORCE_BUSY__ === true
  useEffect(() => {
    const urlForcesBusy = typeof window !== 'undefined' && window.location.search.includes('busy=1');
    const lsForcesBusy = typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('forceBusy') === '1';
    const winForcesBusy = typeof window !== 'undefined' && (window as any).__FORCE_BUSY__ === true;

    if (urlForcesBusy || lsForcesBusy || winForcesBusy) {
      setIsBusy(true);
      (window as any).__FLOW_BUSY__ = true;
    }

    // コンソールから強制切替できるフック
    (window as any).setForceBusy = (on: boolean) => {
      setIsBusy(on);
      (window as any).__FLOW_BUSY__ = on;
    };
  }, []);
  const [contextMenu, setContextMenu] = useState<{x: number, y: number, nodeId: string} | null>(null);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isAddNodeModalOpen, setIsAddNodeModalOpen] = useState(false);
  const [selectedNodeForAdd, setSelectedNodeForAdd] = useState<{id: string, word: string} | null>(null);
  const [isFabAddModalOpen, setIsFabAddModalOpen] = useState(false);
  const [fabAddType, setFabAddType] = useState<'text' | 'child' | null>(null);
  // 直前の操作が候補生成かどうか（生成直後は最短durationでズーム）
  const postGenerateRef = useRef<boolean>(false);
  // handleGenerateCandidatesの参照を保持するためのref（循環参照回避用）
  const handleGenerateCandidatesRef = useRef<((nodeId: string) => Promise<void>) | null>(null);
  
  // ========================================================================
  // メモ化された値
  // ========================================================================
  
  // ノードの表示・非表示を更新
  const updatedTree = useMemo(() => {
    const treeCopy = JSON.parse(JSON.stringify(tree)) as AITreeResponse;
    updateNodeVisibility(treeCopy, showAllSiblings, false, false);
    return treeCopy;
  }, [tree, showAllSiblings]);

  // ノード追加ハンドラー（モーダル表示版）
  const handleAddChild = useCallback((nodeId: string) => {
    // console.log('handleAddChild呼び出し:', nodeId);
    // ノード情報を取得
    const findNodeById = (node: WordNode, id: string): WordNode | null => {
      if (node.path === id) return node;
      for (const child of node.children) {
        const found = findNodeById(child, id);
        if (found) return found;
      }
      return null;
    };

    const node = findNodeById(tree, nodeId);
    // console.log('見つかったノード:', node);
    if (node) {
      setSelectedNodeForAdd({ id: nodeId, word: node.word || 'ルート' });
      setIsAddNodeModalOpen(true);
      // console.log('モーダルを開く:', nodeId);
      // console.log('モーダル状態:', { isAddNodeModalOpen: true, selectedNodeForAdd: { id: nodeId, word: node.word || 'ルート' } });
    }
  }, [tree]);

  // ノード・エッジを生成
  // 先に候補生成ハンドラを宣言してからノード/エッジ生成に使う
  const handleGenerateCandidatesMemo = useCallback((nodeId: string) => {
    if (handleGenerateCandidatesRef.current) {
      return handleGenerateCandidatesRef.current(nodeId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // refを使用しているため依存配列は空でOK

  const { nodes, edges } = useMemo(() => {
    return toFlowFlat(updatedTree, handleAddChild, handleGenerateCandidatesMemo);
  }, [updatedTree, handleAddChild, handleGenerateCandidatesMemo]);
  
  // レイアウトを適用
  const laidNodes = useMemo(() => applyDagreLayout(nodes, edges), [nodes, edges]);

  // 選択されたパスのエッジをハイライト
  const styledEdges = useMemo(() => {
    if (!selectedNodePath) {
      return edges;
    }

    // 選択されたノードからルートまでのパスを取得
    const pathToRoot = findPathToRoot(selectedNodePath, tree);
    if (!pathToRoot) {
      return edges;
    }

    // パス上のエッジIDを生成
    const pathEdgeIds = new Set<string>();
    for (let i = 0; i < pathToRoot.length - 1; i++) {
      const edgeId = `${pathToRoot[i].path}-${pathToRoot[i + 1].path}`;
      pathEdgeIds.add(edgeId);
    }

    // エッジにスタイルを適用
    return edges.map(edge => ({
      ...edge,
      style: {
        ...edge.style,
        stroke: pathEdgeIds.has(edge.id) ? '#3B82F6' : COLORS.edge.color,
        strokeWidth: pathEdgeIds.has(edge.id) ? 3 : COLORS.edge.strokeWidth,
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: pathEdgeIds.has(edge.id) ? '#3B82F6' : COLORS.edge.color,
      },
    }));
  }, [edges, selectedNodePath, tree]);

  // ========================================================================
  // イベントハンドラー
  // ========================================================================

  // パスから文章を構築
  const buildSentenceFromPath = useCallback((path: WordNode[]): string => {
    // ルートノード（最初のノード）を除外して文章を構築
    if (path.length <= 1) return '';
    return path.slice(1).map(node => node.word || '').join('');
  }, []);

  // ノードクリックハンドラー
  const onNodeClick: NodeMouseHandler = useCallback((event, node) => {
    // コンテキストメニューが開いている場合は閉じる
    if (contextMenu) {
      setContextMenu(null);
      return;
    }
    
    setSelectedNodePath(node.id);
    onNodeSelected && onNodeSelected(node.id);
    
    // 選択されたパスを更新
    const path = findPathToRoot(node.id, tree);
    if (path) {
      setSelectedPath(path);
      if (onNodeSelect) {
        onNodeSelect(path);
      }
    } else {
      setSelectedPath([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onNodeSelect, tree, contextMenu, onNodeSelected]);

  // ツリー更新や focusNodeId 変更時に自動フォーカス
  const reactFlowInstanceRef = useRef<ReturnType<typeof useReactFlow> | null>(null);
  const FlowTreeInner: React.FC<{ onNodeSelect: (nodeId: string) => void }> = ({ onNodeSelect }) => {
    const reactFlowInstance = useReactFlow();
    reactFlowInstanceRef.current = reactFlowInstance;
    const handleNodeSelectWithZoom = useCallback((nodeId: string) => {
      onNodeSelect(nodeId);
      setTimeout(() => {
        reactFlowInstance.fitView({
          nodes: [{ id: nodeId }],
          padding: 0.2,
          maxZoom: 1.0,
          minZoom: 0.3,
          duration: 500,
        });
      }, 100);
    }, [reactFlowInstance, onNodeSelect]);

    return (
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        onNodeSelect={handleNodeSelectWithZoom}
        tree={tree}
      />
    );
  };

  useEffect(() => {
    if (!reactFlowInstanceRef.current) return;
    const instance = reactFlowInstanceRef.current;
    const rawTargetId = focusNodeId || selectedNodePath || '0';

    // 設定: ノード選択時のフォーカス可否
    let allowFocusOnSelect = true;
    try {
      const v = window.localStorage?.getItem('focusOnSelect');
      allowFocusOnSelect = v === null ? true : v === '1';
    } catch (e) { /* noop */ }
    if (!focusNodeId && selectedNodePath && !allowFocusOnSelect) return;

    // ターゲット探索（存在しなければ祖先へフォールバック → 最後はルート）
    const findAvailableTarget = (): string | null => {
      const getNode = (id: string) => instance.getNode?.(id) || null;
      let candidate = rawTargetId;
      while (candidate && !getNode(candidate)) {
        const idx = candidate.lastIndexOf('.');
        if (idx === -1) break;
        candidate = candidate.slice(0, idx);
      }
      if (candidate && getNode(candidate)) return candidate;
      return getNode('0') ? '0' : null;
    };

    const initialTarget = findAvailableTarget();
    if (!initialTarget) return;

    // 過剰な連続fitView抑制: ツリーとノード数もキーに含める
    const treeVersion = `${tree.sessionId || ''}:${tree.createdAt || ''}:${initialTarget}:${laidNodes.length}`;
    if (reactFlowViewportRef.current?.focusedForTreeVersion === treeVersion) return;

    // リトライ付きで確実にfitView
    const delays = [focusNodeId ? 160 : 120, 320, 640, 1000, 1500];
    let cancelled = false;

    const tryFit = (i: number) => {
      if (cancelled || i >= delays.length) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (cancelled) return;
            const target = findAvailableTarget();
            if (!target) { tryFit(i + 1); return; }
            try {
              instance.fitView({
                nodes: [{ id: target }],
                padding: 0.2,
                maxZoom: 1.0,
                minZoom: 0.3,
                duration: postGenerateRef.current ? 1 : 300,
              });
              if (reactFlowViewportRef.current) {
                reactFlowViewportRef.current.focusedForTreeVersion = treeVersion;
              }
              // 一度適用したらフラグを戻す
              if (postGenerateRef.current) {
                postGenerateRef.current = false;
              }
            } catch (e) {
              tryFit(i + 1);
            }
          }, delays[i]);
        });
      });
    };

    tryFit(0);
    return () => { cancelled = true; };
  }, [laidNodes, tree, focusNodeId, selectedNodePath, loadVersion]);

  // ノード右クリックハンドラー
  const onNodeContextMenu: NodeMouseHandler = useCallback((event, node) => {
    event.preventDefault();
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      nodeId: node.id,
    });
  }, []);

  // ツリー再読み込み関数
  const reloadTree = useCallback(async () => {
    if (onTreeReload) {
      onTreeReload();
      // console.log('ツリー再読み込み要求');
    } else {
      // console.warn('onTreeReloadが提供されていません');
    }
  }, [onTreeReload]);

  // モーダルでノード追加を確定
  const handleConfirmAddNode = useCallback(async (word: string) => {
    if (!selectedNodeForAdd || !sessionId) return;

    try {
      if (isBusy) return;
      setIsBusy(true);
      // Reactの状態更新を確実に完了させる（少し長めの遅延）
      await new Promise(resolve => setTimeout(resolve, 10));
      // console.log('モーダル確定 - 子ノード追加:', selectedNodeForAdd.id, word);
      const response = await addNode(sessionId, selectedNodeForAdd.id, word);
      if (response.success) {
        // console.log('子ノード追加成功:', response.message);
        await reloadTree(); // ツリーを再読み込み
      } else {
        // console.error('子ノード追加失敗:', response.message);
      }
    } catch (error) {
      // console.error('子ノード追加エラー:', error);
    } finally {
      setIsAddNodeModalOpen(false);
      setSelectedNodeForAdd(null);
      setIsBusy(false);
      ;(window as any).__FLOW_BUSY__ = false;
    }
  }, [selectedNodeForAdd, sessionId, reloadTree, isBusy]);

  // モーダルを閉じる
  const handleCloseAddNodeModal = useCallback(() => {
    setIsAddNodeModalOpen(false);
    setSelectedNodeForAdd(null);
  }, []);

  // FAB用のモーダル確定ハンドラー
  const handleConfirmFabAddNode = useCallback(async (word: string) => {
    if (!fabAddType || !sessionId) return;

    try {
      if (isBusy) return;
      setIsBusy(true);
      ;(window as any).__FLOW_BUSY__ = true;
      // Reactの状態更新を確実に完了させる（少し長めの遅延）
      await new Promise(resolve => setTimeout(resolve, 100));
      // 選択されているノードを親にする（選択されていない場合はルートノード）
      const parentNodeId = selectedNodePath || '0';
      // console.log(`${fabAddType}ノード追加:`, word, '親ノード:', parentNodeId);
      const response = await addNode(sessionId, parentNodeId, word);
      if (response.success) {
        // console.log(`${fabAddType}ノード追加成功:`, response.message);
        await reloadTree(); // ツリーを再読み込み
      } else {
        // console.error(`${fabAddType}ノード追加失敗:`, response.message);
      }
    } catch (error) {
      // console.error(`${fabAddType}ノード追加エラー:`, error);
    } finally {
      setIsFabAddModalOpen(false);
      setFabAddType(null);
      setIsBusy(false);
      ;(window as any).__FLOW_BUSY__ = false;
    }
  }, [fabAddType, sessionId, reloadTree, selectedNodePath, isBusy]);

  // FAB用のモーダルを閉じる
  const handleCloseFabAddModal = useCallback(() => {
    setIsFabAddModalOpen(false);
    setFabAddType(null);
  }, []);

  // 候補生成（葉ノード用）
  const handleGenerateCandidates = useCallback(async (nodeId: string) => {
    if (!sessionId) return;
    
    try {
      if (isBusy) return;
      setIsBusy(true);
      (window as any).__FLOW_BUSY__ = true;
      // Reactの状態更新を確実に完了させる（少し長めの遅延）
      await new Promise(resolve => setTimeout(resolve, 100));
      // console.log('候補を生成(mock selectNode):', nodeId);
      // 生成対象ノードを選択状態として記録（再読み込み後のフォーカス用）
      setSelectedNodePath(nodeId);
      const path = findPathToRoot(nodeId, tree);
      if (path) {
        setSelectedPath(path);
      } else {
        setSelectedPath([]);
      }
      onNodeSelected && onNodeSelected(nodeId);
      // 次回fitViewは生成直後として最短durationに
      postGenerateRef.current = true;
      const response = await selectNode(sessionId, nodeId);
      if (response.success) {
        // console.log('候補生成成功:', response.message);
        await reloadTree();
      } else {
        if (isDummyEndError(response.error)) {
          await reloadTree();
        } else {
          // console.error('候補生成失敗:', response.message);
        }
      }
    } catch (error) {
      // console.error('候補生成エラー:', error);
    } finally {
      setIsBusy(false);
      ;(window as any).__FLOW_BUSY__ = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, reloadTree, isBusy, isDummyEndError, onNodeSelected, tree]);

  // handleGenerateCandidatesをrefに設定（循環参照回避のため）
  useEffect(() => {
    handleGenerateCandidatesRef.current = handleGenerateCandidates;
  }, [handleGenerateCandidates]);

  const handleAddSibling = useCallback(async (nodeId: string) => {
    if (!sessionId) return;
    
    try {
      // console.log('兄弟ノード追加:', nodeId);
      const response = await addNode(sessionId, nodeId, '新しい兄弟ノード');
      if (response.success) {
        // console.log('兄弟ノード追加成功:', response.message);
        await reloadTree(); // ツリーを再読み込み
      } else {
        // console.error('兄弟ノード追加失敗:', response.message);
      }
    } catch (error) {
      // console.error('兄弟ノード追加エラー:', error);
    }
  }, [sessionId, reloadTree]);

  const handleDelete = useCallback(async (nodeId: string) => {
    if (!sessionId) return;
    
    try {
      if (isBusy) return;
      setIsBusy(true);
      // Reactの状態更新を確実に完了させる（少し長めの遅延）
      await new Promise(resolve => setTimeout(resolve, 100));
      // console.log('ノード削除:', nodeId);
      const response = await deleteNode(sessionId, nodeId);
      if (response.success) {
        // console.log('ノード削除成功:', response.message);
        await reloadTree(); // ツリーを再読み込み
      } else {
        // console.error('ノード削除失敗:', response.message);
      }
    } catch (error) {
      // console.error('ノード削除エラー:', error);
    } finally {
      setIsBusy(false);
      ;(window as any).__FLOW_BUSY__ = false;
    }
  }, [sessionId, reloadTree, isBusy]);

  const handleAddTextNode = useCallback(() => {
    setFabAddType('text');
    setIsFabAddModalOpen(true);
  }, []);

  const handleAddChildNode = useCallback(() => {
    setFabAddType('child');
    setIsFabAddModalOpen(true);
  }, []);

  
  const handleAiGenerateX10 = useCallback(async () => {
    if (!sessionId || !selectedNodePath) return;
    
    try {
      if (isBusy) return;
      setIsBusy(true);
      ;(window as any).__FLOW_BUSY__ = true;
      // Reactの状態更新を確実に完了させる（少し長めの遅延）
      await new Promise(resolve => setTimeout(resolve, 100));
      // console.log('AI生成×10開始:', selectedNodePath);
      const response = await aiGenerateX10(sessionId, selectedNodePath);
      if (response.success) {
        // console.log('AI生成×10成功:', response.message);
        await reloadTree();
      } else {
        // console.error('AI生成×10失敗:', response.message);
      }
    } catch (error) {
      // console.error('AI生成×10エラー:', error);
    } finally {
      setIsBusy(false);
      ;(window as any).__FLOW_BUSY__ = false;
    }
  }, [sessionId, selectedNodePath, reloadTree, isBusy]);

  

  // AI生成×60（内部処理用、フォーカスなし）
  const handleAiGenerateX60Internal = useCallback(async () => {
    if (!sessionId) return;
    
    try {
      // console.log('AI生成×60（内部処理）開始 - 直接60回のAI生成を実行');
      // console.log('aiGenerateX60を呼び出し、内部で60回のAI生成が実行されます');
      
      const response = await aiGenerateX60(sessionId, '0'); // 常にルートノードから開始
      
      // console.log(`aiGenerateX60呼び出し完了`);
      // console.log(`レスポンス:`, response);
      
      if (!response.success) {
        // console.error('AI生成×60（内部処理）失敗:', response.message);
        return;
      }
      
      // console.log('AI生成×60（内部処理）完了 - 合計60回のAI生成を実行');
      
      // AI生成完了後にツリーを再読み込み
      // console.log('ツリーを再読み込み中...');
      await reloadTree();
      // console.log('ツリー再読み込み完了');
      
    } catch (error) {
      // console.error('AI生成×60（内部処理）エラー:', error);
    }
  }, [sessionId, reloadTree]);

  // 自動処理: AI生成×60を実行してからチャットを開く
  useEffect(() => {
    if (autoOpenChatAndGenerate && tree && sessionId) {
      // 少し遅延してから実行（ツリーの描画完了を待つ）
      const timer = setTimeout(async () => {
        // console.log('自動処理開始: AI生成×60を実行してからチャットを開く');
        
        // AI生成×60を実行（フォーカスなしの内部処理）
        await handleAiGenerateX60Internal();
        
        // AI生成完了後にチャットを開く
        // console.log('AI生成×60完了後、チャットを開きます');
        setIsChatDrawerOpen(true);
        setSelectedNodePath('0');
        const rootPath = findPathToRoot('0', tree);
        if (rootPath) {
          setSelectedPath(rootPath);
        } else {
          setSelectedPath([]);
        }
        onNodeSelected?.('0');
        
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [autoOpenChatAndGenerate, tree, sessionId, handleAiGenerateX60Internal, onNodeSelected]);

  const handleToggleChat = useCallback(() => {
    setIsChatDrawerOpen(prev => !prev);
  }, []);

  // 検索機能のハンドラー
  const handleSearch = useCallback(() => {
    setIsSearchModalOpen(true);
  }, []);

  const handleSearchNodeSelect = useCallback((nodeId: string) => {
    setSelectedNodePath(nodeId);
    const path = findPathToRoot(nodeId, tree);
    if (path) {
      setSelectedPath(path);
    } else {
      setSelectedPath([]);
    }

    if (onNodeSelect) {
      const path = findPathToRoot(nodeId, tree);
      if (path) {
        onNodeSelect(path);
      }
    }
  }, [onNodeSelect, tree]);

  // 背景クリックでメニューを閉じる
  const onPaneClick = useCallback(() => {
    if (contextMenu) {
      setContextMenu(null);
    }
  }, [contextMenu]);

  // ========================================================================
  // スタイル適用
  // ========================================================================

  // 選択されたノードのスタイルを適用
  const styledNodes = useMemo(() => {
    return laidNodes.map(node => {
      const isSelected = node.id === selectedNodePath;
      return {
        ...node,
        style: {
          ...node.style,
          // borderショートハンドではなく、個別プロパティを使用
          borderColor: isSelected 
            ? COLORS.selectedPath.border 
            : (node.style?.borderColor || COLORS.default.border),
          borderWidth: isSelected ? 3 : (node.style?.borderWidth || 1),
          borderStyle: 'solid',
          boxShadow: isSelected 
            ? COLORS.selectedPath.shadow 
            : 'none'
        }
      };
    });
  }, [laidNodes, selectedNodePath]);

  // ========================================================================
  // レンダリング
  // ========================================================================

  // FlowTreeInner は上で再定義済み

  return (
    <div className="w-full h-full relative">
      {/* 右上インフォバー（処理中） */}
      {isBusy && (
        <div className="absolute top-3 right-12 z-50 bg-blue-600 text-white text-sm rounded-full px-3 py-1.5 shadow flex items-center">
          <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
          </svg>
          バックエンド処理中…
        </div>
      )}
      <ReactFlow 
        nodes={styledNodes} 
        edges={styledEdges} 
        fitView 
        fitViewOptions={REACT_FLOW_CONFIG.fitViewOptions}
        nodesDraggable={false} 
        panOnScroll 
        selectionOnDrag={false}
        onNodeClick={onNodeClick}
        onNodeContextMenu={onNodeContextMenu}
        onPaneClick={onPaneClick}
      >
        <Background 
          gap={REACT_FLOW_CONFIG.backgroundGap} 
          color={REACT_FLOW_CONFIG.backgroundColor} 
        />
        <Controls 
          showInteractive={false} 
          position="top-right" 
        />
        <FlowTreeInner onNodeSelect={handleSearchNodeSelect} />
      </ReactFlow>

      {/* コンテキストメニュー */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          nodeId={contextMenu.nodeId}
          onClose={() => setContextMenu(null)}
          onAddChild={handleAddChild}
          onAddSibling={handleAddSibling}
          onDelete={handleDelete}
          showGenerate={(() => {
            const findNodeById = (node: WordNode, id: string): WordNode | null => {
              if (node.path === id) return node;
              for (const child of node.children) {
                const found = findNodeById(child, id);
                if (found) return found;
              }
              return null;
            };
            const n = findNodeById(tree, contextMenu.nodeId);
            return !!n && n.children.length === 0;
          })()}
          onGenerateCandidates={handleGenerateCandidates}
        />
      )}

      {/* フローティングアクションボタン */}
        <FloatingActionButton
          onToggleSiblings={() => onToggleShowAll && onToggleShowAll(!showAllSiblings)}
          onSearch={handleSearch}
          onAddTextNode={handleAddTextNode}
          onAddChildNode={handleAddChildNode}
          onAiGenerateX10={handleAiGenerateX10}
          onToggleChat={handleToggleChat}
          showAllSiblings={showAllSiblings}
          isBusy={isBusy}
        />

      {/* ノード追加モーダル */}
      {selectedNodeForAdd && (
        <AddNodeModal
          isOpen={isAddNodeModalOpen}
          onClose={handleCloseAddNodeModal}
          onConfirm={handleConfirmAddNode}
          parentNodeId={selectedNodeForAdd.id}
          parentNodeWord={selectedNodeForAdd.word}
          nodeType="child"
        />
      )}

      {/* FAB用のノード追加モーダル */}
      {fabAddType && (
        <AddNodeModal
          isOpen={isFabAddModalOpen}
          onClose={handleCloseFabAddModal}
          onConfirm={handleConfirmFabAddNode}
          parentNodeId={selectedNodePath || '0'}
          parentNodeWord={selectedNodePath ? 
            (() => {
              const findNodeById = (node: WordNode, id: string): WordNode | null => {
                if (node.path === id) return node;
                for (const child of node.children) {
                  const found = findNodeById(child, id);
                  if (found) return found;
                }
                return null;
              };
              const node = findNodeById(tree, selectedNodePath);
              return node?.word || '選択されたノード';
            })() : 'ルート'
          }
          nodeType={fabAddType}
        />
      )}

      {/* チャット画面 */}
      <ChatDrawer
        isOpen={isChatDrawerOpen}
        onClose={() => setIsChatDrawerOpen(false)}
        selectedNodePath={selectedNodePath}
        treeData={tree}
        sessionPrompt={tree?.prompt || ''}
      />

      {/* 選択されたノードのルートからの文章を表示 */}
      {selectedPath.length > 0 && (
        <div className={`fixed bottom-0 z-50 p-4 bg-blue-50 border-t border-blue-200 shadow-lg transition-all duration-300 ${
          sidebarOpen 
            ? 'left-64 lg:left-64' // サイドバーが開いているとき（大画面では常に開いている）
            : 'left-0 lg:left-64'   // サイドバーが閉じているとき（小画面のみ、大画面では常に開いている）
        } right-0`}>
          <div className="max-w-6xl mx-auto">
            <div className="space-y-3">
              <div className="flex items-center space-x-2 text-sm text-blue-700">
                <span className="font-medium">構成単語:</span>
                <span className="bg-white px-2 py-1 rounded border">
                  {selectedPath.slice(1).map((node, index) => (
                    <span key={node.path}>
                      <span className="font-semibold">{node.word}</span>
                      {index < selectedPath.slice(1).length - 1 && (
                        <span className="text-blue-400 mx-1">→</span>
                      )}
                    </span>
                  ))}
                </span>
              </div>
              <div className="text-sm text-blue-700">
                
                <div className="bg-white px-3 py-2 rounded border font-mono text-base whitespace-pre-wrap break-words">
                  {buildSentenceFromPath(selectedPath)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowTree;
